/**
 * Task #583 — Trusted Devices page.
 *
 * Lists every device currently trusted for the signed-in super-admin
 * with revoke buttons and a "Revoke all other devices" panic action.
 *
 * The current session's device is highlighted and refuses revocation
 * (revoking it would sign you out and require a fresh approval).
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { Laptop, Loader2, Trash2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PlatformLayout } from "./platform-layout";

interface TrustedDevice {
  id: string;
  label: string;
  firstSeenAt: string;
  lastSeenAt: string;
  lastIp: string | null;
  lastUserAgent: string | null;
  approvedByEmail: string | null;
  isCurrent: boolean;
}

export function DevicesContent() {
  const { toast } = useToast();
  const { data: devices, isLoading } = useQuery<TrustedDevice[]>({
    queryKey: ["/api/platform/devices"],
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/platform/devices/${id}/revoke`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Device revoked" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/devices"] });
    },
    onError: (err: any) =>
      toast({ title: "Could not revoke device", description: err.message, variant: "destructive" }),
  });

  const revokeOthers = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/devices/revoke-others");
      return res.json();
    },
    onSuccess: (data: { devicesRevoked: number; sessionsKilled: number }) => {
      toast({
        title: "Other devices revoked",
        description: `${data.devicesRevoked} device(s) revoked, ${data.sessionsKilled} session(s) ended.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/devices"] });
    },
    onError: (err: any) =>
      toast({ title: "Could not revoke devices", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Laptop className="h-5 w-5" /> Trusted Devices
          </h2>
          <p className="text-sm text-muted-foreground">
            Browsers and devices that can sign in to the platform admin without a fresh approval.
          </p>
        </div>
        {devices && devices.length > 1 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" data-testid="button-revoke-others">
                <ShieldAlert className="h-4 w-4 mr-2" /> Revoke all other devices
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Revoke every other device?</AlertDialogTitle>
                <AlertDialogDescription>
                  All trusted devices except the one you're using right now will be revoked and any
                  active sessions on them will be ended. The next sign-in from each of those browsers
                  will require a fresh emailed code and another super-admin's approval.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-revoke-others-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => revokeOthers.mutate()}
                  data-testid="button-revoke-others-confirm"
                >
                  Revoke all other devices
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!isLoading && (!devices || devices.length === 0) && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground" data-testid="text-no-devices">
            No trusted devices yet.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {devices?.map((d) => (
          <Card key={d.id} data-testid={`card-device-${d.id}`}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-base flex items-center gap-2" data-testid={`text-device-label-${d.id}`}>
                  {d.label}
                  {d.isCurrent && <Badge className="bg-green-600">This device</Badge>}
                </CardTitle>
                <CardDescription>
                  First seen {new Date(d.firstSeenAt).toLocaleString()} · last active{" "}
                  {new Date(d.lastSeenAt).toLocaleString()}
                </CardDescription>
              </div>
              {!d.isCurrent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => revoke.mutate(d.id)}
                  disabled={revoke.isPending}
                  data-testid={`button-revoke-${d.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Revoke
                </Button>
              )}
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-1">
              <div>IP: {d.lastIp || "unknown"}</div>
              <div className="break-all">Browser: {d.lastUserAgent ? d.lastUserAgent.slice(0, 200) : "unknown"}</div>
              <div>
                Approved by:{" "}
                {d.approvedByEmail || <span className="italic">bootstrap (auto-trusted, first device)</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function PlatformDevices() {
  return <PlatformLayout><DevicesContent /></PlatformLayout>;
}
